import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Dialog,
  Typography,
  Box,
  Grid,
  Button,
} from '@material-ui/core';
import {
  lastMinuteDealsRequest,
  lastMinuteDealsDelete,
  lastMinuteDealsCreate,
} from 'actions/cottage';
import { showSnackbar } from 'actions';
import {
  lastMinuteDealsData,
  lastMinuteDealsIsLoading,
  lastMinuteCreateDeleteStatus,
} from 'selectors/cottage';
import DialogTemplate from 'components/shared/DialogTemplate';
import ConfirmationButton from 'components/shared/ConfirmationButton';
import moment from 'logic/moment';
import { validateUpdated, cleanValues } from 'logic/lastMinutedDeals';
import { DatePicker, Input } from 'components/FormInputs';
import { useFormik } from 'components/shared/Formik';

const maxFromDate = moment().add(2, 'months').subtract(1, 'day').format('YYYY-MM-DD');
const maxToDate = moment().add(2, 'months').format('YYYY-MM-DD');
const labels = {
  from_date: (
    <FormattedMessage
      id="From"
      defaultMessage="From"
    />
  ),
  to_date: (
    <FormattedMessage
      id="Until"
      defaultMessage="Until"
    />
  ),
  price: (
    <FormattedMessage
      id="Price"
      defaultMessage="Price"
    />
  ),
  title_sv: (
    <FormattedMessage
      id="Description Swedish"
      defaultMessage="Description Swedish"
    />
  ),
  title_en: (
    <FormattedMessage
      id="Description English"
      defaultMessage="Description English"
    />
  ),
  title_de: (
    <FormattedMessage
      id="Description German"
      defaultMessage="Description German"
    />
  ),
};

const DealValue = ({ label, value }) => (
  <div>
    <Typography variant="subtitle2">{label}</Typography>
    <Typography>{value}</Typography>
  </div>
);

DealValue.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
};

const Deal = ({ deal, onDelete, isLoading }) => (
  <Box>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <DealValue
          label={labels.from_date}
          value={moment(deal.from_date).format('LL')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DealValue
          label={labels.to_date}
          value={moment(deal.to_date).format('LL')}
        />
      </Grid>
      <Grid item xs={12}>
        <DealValue
          label={labels.price}
          value={deal.price}
        />
      </Grid>
      <Grid item xs={12}>
        <DealValue
          label={labels.title_sv}
          value={deal.title_sv}
        />
      </Grid>
      <Grid item xs={12}>
        <DealValue
          label={labels.title_en}
          value={deal.title_en}
        />
      </Grid>
      <Grid item xs={12}>
        <DealValue
          label={labels.title_de}
          value={deal.title_de}
        />
      </Grid>
    </Grid>
    <Box mt={2} textAlign="right">
      <ConfirmationButton
        variant="contained"
        color="secondary"
        onConfirm={() => onDelete(deal.id)}
        disabled={isLoading}
      >
        <FormattedMessage
          id="Delete"
          defaultMessage="Delete"
        />
      </ConfirmationButton>
    </Box>
  </Box>
);

Deal.propTypes = {
  deal: PropTypes.shape({
    id: PropTypes.number,
    title_en: PropTypes.string,
    title_sv: PropTypes.string,
    title_de: PropTypes.string,
    from_date: PropTypes.string,
    to_date: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onDelete: PropTypes.func,
  isLoading: PropTypes.bool,
};

const LastMinuteDialog = ({ cottageId }) => {
  const intl = useIntl();
  const [open, setIsOpen] = React.useState(false);
  const dispatch = useDispatch();
  const data = useSelector(lastMinuteDealsData);
  const isLoading = useSelector(lastMinuteDealsIsLoading);
  const { created, deleted } = useSelector(lastMinuteCreateDeleteStatus);

  React.useEffect(() => {
    if (created) {
      dispatch(showSnackbar({
        severity: 'success',
        message: {
          id: 'lastminute.created',
          defaultMessage: 'The last minute deal was created',
        },
      }));
    }
  }, [created]);

  React.useEffect(() => {
    if (deleted) {
      dispatch(showSnackbar({
        severity: 'success',
        message: {
          id: 'lastminute.deleted',
          defaultMessage: 'The last minute deal was deleted',
        },
      }));
    }
  }, [deleted]);

  const onSave = (values) => {
    dispatch(lastMinuteDealsCreate({ cottageId, values }));
  };

  const formik = useFormik({
    initialValues: {
      from_date: null,
      to_date: null,
      price: '',
      title_sv: '',
      title_de: '',
      title_en: '',
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: false,
    validate: validateUpdated({ intl }),
    onSubmit: async (values) => {
      return onSave(cleanValues(values));
    },
  });

  React.useEffect(() => {
    if (open && !isLoading) {
      dispatch(lastMinuteDealsRequest({ cottageId }));
    }
  }, [open]);

  const onDelete = (lastMinuteId) => {
    dispatch(lastMinuteDealsDelete({ cottageId, lastMinuteId }));

    setIsOpen(false);
    formik.resetForm();
  };

  const openDialog = () => setIsOpen(true);

  const closeDialog = () => setIsOpen(false);

  const deal = data && data[0] ? data[0] : null;

  const {
    values,
    errors,
    setFieldValue,
    handleChange,
    handleSubmit,
    isSubmitting,
    isValid,
    submitForm,
    pristine,
    touched,
    handleBlur,
  } = formik;

  return (
    <>
      <Dialog open={open} onClose={closeDialog}>
        <DialogTemplate
          pristine={pristine || !isValid || isSubmitting || !touched}
          isUpdating={isLoading}
          onClose={closeDialog}
          onSave={!deal ? submitForm : null}
        >
          <Box mb={2} px={2}>
            <form onSubmit={handleSubmit}>
              {
                !!deal && <Deal deal={deal} onDelete={onDelete} isLoading={isLoading} />
              }
              {
                !deal && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        name="from_date"
                        value={values.from_date || null}
                        onBlur={handleBlur}
                        onChange={(date) => {
                          if (date) {
                            setFieldValue('from_date', date);
                          }

                          if (date === null) {
                            setFieldValue('from_date', null);
                          }
                        }}
                        label={labels.from_date}
                        cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
                        clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
                        errors={errors}
                        minDate={moment().format('YYYY-MM-DD')}
                        maxDate={maxFromDate}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        name="to_date"
                        value={values.to_date || null}
                        onBlur={handleBlur}
                        onChange={(date) => {
                          if (date) {
                            setFieldValue('to_date', date);
                          }

                          if (date === null) {
                            setFieldValue('to_date', null);
                          }
                        }}
                        label={labels.to_date}
                        cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
                        clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
                        errors={errors}
                        minDate={values.from_date}
                        maxDate={maxToDate}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        name="price"
                        type="number"
                        label={labels.price}
                        errors={errors}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.price}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        name="title_sv"
                        label={labels.title_sv}
                        errors={errors}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title_sv}
                        inputProps={{ maxLength: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        name="title_en"
                        label={labels.title_en}
                        errors={errors}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title_en}
                        inputProps={{ maxLength: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        name="title_de"
                        label={labels.title_de}
                        errors={errors}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title_de}
                        inputProps={{ maxLength: 50 }}
                      />
                    </Grid>
                  </Grid>
                )
              }
            </form>
          </Box>
        </DialogTemplate>
      </Dialog>
      <Button
        size="small"
        onClick={openDialog}
        disabled={isLoading}
      >
        <FormattedMessage
          id="cottages.createLastMinute"
          defaultMessage="Create last minute deal"
        />
      </Button>
    </>
  );
};

LastMinuteDialog.propTypes = {
  cottageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

};

export default LastMinuteDialog;
